import { useState, useEffect, FormEvent } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { KeyRound, Eye, EyeOff, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import logoEspaceClientTexte from '../images/Logo-Espace-client-texte.png'
import logoEspaceClientTexteDark from '../images/Logo-Espace-client-texte-dark.png'

interface InvitationInfo {
  email: string
  firstname: string
  lastname: string
  customer_name: string
  erp_name: string
  proposal_id: string
}

export default function AcceptInvitation() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') ?? ''

  const [info, setInfo] = useState<InvitationInfo | null>(null)
  const [loadError, setLoadError] = useState('')
  const [loading, setLoading] = useState(true)

  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConf, setPasswordConf] = useState('')
  const [showPwd, setShowPwd] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    if (!token) {
      setLoadError(t('acceptInvitation.noToken'))
      setLoading(false)
      return
    }
    api
      .checkInvitation(token)
      .then((data) => {
        setInfo(data)
        setFirstname(data.firstname ?? '')
        setLastname(data.lastname ?? '')
      })
      .catch(() => setLoadError(t('acceptInvitation.invalidToken')))
      .finally(() => setLoading(false))
  }, [token, t])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitError('')

    if (password.length < 8) {
      setSubmitError(t('acceptInvitation.passwordTooShort'))
      return
    }
    if (password !== passwordConf) {
      setSubmitError(t('acceptInvitation.passwordMismatch'))
      return
    }

    setSubmitting(true)
    try {
      const result = await api.acceptInvitation({
        token,
        password,
        firstname,
        lastname,
      })
      // Store auth credentials
      localStorage.setItem('auth_token', result.token)
      localStorage.setItem('user', JSON.stringify(result.user))

      // Redirect to the proposal if any, otherwise dashboard
      if (result.proposal_id) {
        navigate(`/proposals/${result.proposal_id}`)
      } else {
        navigate('/dashboard')
      }
    } catch (err) {
      const apiError = err as { response?: { data?: { error?: string } } }
      setSubmitError(apiError?.response?.data?.error ?? t('acceptInvitation.submitError'))
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <RefreshCw size={24} className="animate-spin text-blue-500" />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {t('acceptInvitation.errorTitle')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">{loadError}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <Link
            to="/"
            aria-label={t('common.backToHome')}
            title={t('common.backToHome')}
            className="group inline-flex items-center rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 mb-4"
          >
            <img
              src={logoEspaceClientTexte}
              alt="Espace Client"
              className="h-10 w-auto transition-transform group-hover:scale-105 dark:hidden"
            />
            <img
              src={logoEspaceClientTexteDark}
              alt=""
              aria-hidden="true"
              className="hidden h-10 w-auto transition-transform group-hover:scale-105 dark:block"
            />
          </Link>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('acceptInvitation.title')}
          </h1>
          {info && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('acceptInvitation.subtitle', {
                company: info.customer_name,
                erp: info.erp_name,
              })}
            </p>
          )}
        </div>

        {info?.proposal_id && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-700 dark:text-blue-300">
            <CheckCircle size={16} />
            {t('acceptInvitation.proposalHint')}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                {t('common.firstname')}
              </span>
              <input
                required
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                {t('common.lastname')}
              </span>
              <input
                required
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
              {t('common.email')}
            </span>
            <input
              readOnly
              value={info?.email ?? ''}
              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-3 py-2 text-sm cursor-not-allowed"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
              {t('common.password')}
            </span>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPwd((s) => !s)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <span className="text-xs text-gray-400 mt-0.5 block">
              {t('acceptInvitation.passwordHint')}
            </span>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
              {t('acceptInvitation.confirmPassword')}
            </span>
            <input
              type={showPwd ? 'text' : 'password'}
              required
              value={passwordConf}
              onChange={(e) => setPasswordConf(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
            />
          </label>

          {submitError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
              <AlertCircle size={15} /> {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded-lg text-sm transition-colors"
          >
            <KeyRound size={16} />
            {submitting ? t('common.loading') : t('acceptInvitation.submitBtn')}
          </button>
        </form>
      </div>
    </div>
  )
}
